import { dialsFromKnobs, effectiveDials } from './dials.ts';
import type { BotProfileDocument, BotProfileKnobs } from './types.ts';

/**
 * A bot's character at a glance: six 0–100 traits. Skill, Consistency, Aggression, and Manners
 * are the workshop's draggable bars (`BotDials`); Foresight and Precision only follow from them.
 */
export type BotTraits = {
  /** Overall closeness to Expert's pace. */
  skill: number;
  /** How alike one race is to the next: little aim error (what crashes and scatters races). */
  consistency: number;
  /** Squeezing, squeeze tries, and the will to block. */
  aggression: number;
  /** Reading rivals, following distance, holding blocks back. */
  manners: number;
  /** Turns planned ahead, and whether it sees bends coming. */
  foresight: number;
  /** Few slips, little aim error. */
  precision: number;
};

/** The bars a player drags, in chart order. */
export const DRAGGABLE_TRAIT_KEYS = ['skill', 'consistency', 'aggression', 'manners'] as const;
/** The bars that only follow from the others. */
export const READOUT_TRAIT_KEYS = ['foresight', 'precision'] as const;

export const BOT_TRAIT_KEYS = [
  ...DRAGGABLE_TRAIT_KEYS,
  ...READOUT_TRAIT_KEYS,
] as const satisfies readonly (keyof BotTraits)[];

export { estimatedPace } from './paceModel.ts';

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
const percent = (value: number) => Math.round(clamp01(value) * 100);

export function botTraits(knobs: BotProfileKnobs): BotTraits {
  const slips = clamp01(knobs.temperature / 1.5);
  const aim = clamp01(knobs.aimErrorM / 6);

  return {
    ...dialsFromKnobs(knobs),
    foresight: percent(Math.min(knobs.depth, 10) / 10 * 0.8 + (knobs.courseSense === 'time' ? 0.2 : 0)),
    precision: percent(1 - 0.5 * slips - 0.5 * aim),
  };
}

/** A profile's traits: its bars as last dragged when it has them, else read off its knobs. */
export function profileTraits(profile: BotProfileDocument): BotTraits {
  const traits = botTraits(profile.knobs);
  return profile.dials ? { ...traits, ...effectiveDials(profile.dials) } : traits;
}
